'use client'

import { useRef, type ReactNode } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { ArrowRight, Code, Database, Globe, Layers, Rocket, Shield, Sparkles, Users, Zap, Box, Cpu } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback, Button, Orbital } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  reducedMotionVariants,
} from '../../lib/motion'

/**
 * CtaOrbiting - CTA with orbiting elements
 *
 * Features animated orbiting circles around a centered CTA.
 * Supports both avatars (for community/social) and icons (for features/tech).
 * Great for community, membership, product features, or tech stack displays.
 */

export interface OrbitItem {
  /** Type of orbit item */
  type: 'avatar' | 'icon'
  /** Avatar image source URL (for type: 'avatar') */
  src?: string
  /** Fallback text/initials (for type: 'avatar') */
  fallback?: string
  /** Icon element (for type: 'icon') */
  icon?: ReactNode
  /** Alt text for accessibility */
  alt?: string
}

/**
 * Props for CtaOrbiting component
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Centered layout with animated orbiting elements (avatars or icons)
 * - Single button (1 regular button)
 * - **NEVER use this for install commands - use CtaCenteredCheck instead**
 * - Best for community/membership CTAs or tech stack showcase
 * - Supports 3 orbit levels (inner, middle, outer)
 *
 * **CTA Button Rules (CRITICAL):**
 * 1. Use this component for 1 regular button
 * 2. Use CtaCenteredCheck for npm/npx/pnpm install commands (InstallSnippet ONLY)
 * 3. NEVER mix InstallSnippet with regular buttons - they are mutually exclusive
 */
export interface CtaOrbitingProps {
  /** Main heading text */
  heading?: string
  /** Description text */
  description?: string
  /** Button configuration */
  button?: {
    text: string
    url: string
  }
  /** Inner orbit items (closest to center) */
  innerOrbit?: OrbitItem[]
  /** Middle orbit items */
  middleOrbit?: OrbitItem[]
  /** Outer orbit items (furthest from center) */
  outerOrbit?: OrbitItem[]
  className?: string
}

// Default icons representing a developer platform/community
const defaultInnerOrbit: OrbitItem[] = [
  { type: 'icon', icon: <Code className="size-5" /> },
  { type: 'icon', icon: <Database className="size-5" /> },
  { type: 'icon', icon: <Globe className="size-5" /> },
]

const defaultMiddleOrbit: OrbitItem[] = [
  { type: 'icon', icon: <Rocket className="size-5" /> },
  { type: 'icon', icon: <Shield className="size-5" /> },
  { type: 'icon', icon: <Sparkles className="size-5" /> },
]

const defaultOuterOrbit: OrbitItem[] = [
  { type: 'icon', icon: <Users className="size-5" /> },
  { type: 'icon', icon: <Zap className="size-5" /> },
  { type: 'icon', icon: <Layers className="size-5" /> },
  { type: 'icon', icon: <Box className="size-5" /> },
  { type: 'icon', icon: <Cpu className="size-5" /> },
]

function OrbitItemRenderer({ item }: { item: OrbitItem }) {
  if (item.type === 'avatar') {
    return (
      <Avatar className="size-10">
        {item.src && <AvatarImage src={item.src} alt={item.alt || ''} />}
        <AvatarFallback className="text-xs">{item.fallback}</AvatarFallback>
      </Avatar>
    )
  }

  // Icon type
  return (
    <div className="flex size-10 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm">
      {item.icon}
    </div>
  )
}

const CtaOrbiting = ({
  heading = 'Join Our Community',
  description = 'Connect with developers from around the world. Share knowledge, collaborate on projects, and grow together.',
  button = {
    text: 'Become a member',
    url: '#',
  },
  innerOrbit = defaultInnerOrbit,
  middleOrbit = defaultMiddleOrbit,
  outerOrbit = defaultOuterOrbit,
  className,
}: CtaOrbitingProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto relative">
        <div className="flex w-full items-center justify-center">
          <div className="absolute z-10 flex h-full w-full flex-col items-center justify-center gap-4 px-4">
            <div className="pointer-events-none absolute inset-y-0 left-1/2 top-1/2 h-1/3 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 bg-background blur-2xl" />
            <motion.h1
              className="z-10 text-center text-5xl text-balance font-semibold tracking-tighter sm:text-6xl"
              variants={headerAnimation}
            >
              {heading}
            </motion.h1>
            <motion.div
              className="z-10 max-w-2xl text-center"
              variants={headerAnimation}
            >
              <Markdown size="base">{description}</Markdown>
            </motion.div>

            <motion.div className="z-10 w-full sm:w-auto" variants={headerAnimation}>
              <Button
                asChild
                variant="default"
                className="group w-full sm:w-auto"
              >
                <a href={button.url}>
                  {button.text}
                  <ArrowRight className="-rotate-45 rounded-full transition-all ease-in-out group-hover:rotate-0" />
                </a>
              </Button>
            </motion.div>
          </div>
          <div className="relative flex h-[620px] w-full flex-col items-center justify-center overflow-hidden">
            <Orbital itemSize={40} radius={130} speed={2}>
              {innerOrbit.map((item, index) => (
                <OrbitItemRenderer key={index.toString()} item={item} />
              ))}
            </Orbital>
            <Orbital itemSize={40} radius={200} reverse speed={2}>
              {middleOrbit.map((item, index) => (
                <OrbitItemRenderer key={index.toString()} item={item} />
              ))}
            </Orbital>
            <Orbital itemSize={40} radius={280} speed={1}>
              {outerOrbit.map((item, index) => (
                <OrbitItemRenderer key={index.toString()} item={item} />
              ))}
            </Orbital>
          </div>
        </div>
      </div>
    </motion.section>
  )
}

export { CtaOrbiting }
